import type { DataTableRow } from "@bcc/components/data-table/data-table.types";
import type { CarrierCustomerAccountBinding, PurchaseOrder } from "@shared/types";
import { readableBatchId } from "@client-shared/lib/util";
import { sortByCreatedAt, sortByNumber } from "@client-shared/utils/table-sorting";
import type { ColumnDef } from "@tanstack/react-table";
import { parseISO } from "date-fns";
import type { TFunction } from "i18next";
import {
  OrderAgeCell,
  OrderBatchCell,
  OrderCreatedAtCell,
  OrderHeaderCell,
  OrderIdCell,
  OrderItemsCell,
  OrderMarketplaceCell,
  OrderShipToCountryCell,
  OrderShipToFlagsCell,
  OrderStatusCell,
  OrderTrackingCell,
  OrderValueCell,
  OrderWeightCell,
} from "../cells";
import { getShipToFlagCountryCode } from "../cells/order-ship-to-flags-cell.utils";
import { ORDER_COLUMN_IDS, ORDER_STATUS_CONFIG } from "../constants";
import { computeOrderAge } from "../utils/compute-order-age";
import { getMarketplaceDisplayName } from "../utils/get-marketplace-display-name";
import { compareByFulfillmentThenCreatedDesc } from "./orders-sort.utils";

interface OrderColumnCallbacks {
  onOrderClick?: (order: PurchaseOrder) => void;
  onBatchClick?: (batchId: string) => void;
  carrierDetails: CarrierCustomerAccountBinding[];
  t: TFunction;
  /**
   * When true, include the ship-to country column. Driven by whether the
   * vendor's `properties.shipTo` is present in the API response — cross-border
   * vendors only.
   */
  showShipToCountry?: boolean;
  /**
   * When true, include the ship-to flags column (single flag per order,
   * derived from `shippingAddress`). Used by the batch orders table only.
   */
  showShipToFlags?: boolean;
}

export function getOrderColumns({
  onOrderClick,
  onBatchClick,
  carrierDetails,
  t,
  showShipToCountry = false,
  showShipToFlags = false,
}: OrderColumnCallbacks): ColumnDef<DataTableRow<PurchaseOrder>>[] {
  const shipToColumn: ColumnDef<DataTableRow<PurchaseOrder>> = {
    id: ORDER_COLUMN_IDS.SHIP_TO_COUNTRY,
    accessorFn: (row) => row.shippingAddress?.countryCode ?? row.shippingAddress?.country ?? "",
    header: OrderHeaderCell,
    cell: OrderShipToCountryCell,
    enableGrouping: true,
    getGroupingValue: (row) => row.shippingAddress?.countryCode ?? row.shippingAddress?.country ?? "",
    meta: { exportHeader: t("views.orders.columns.shipToCountry") },
  };

  const shipToFlagsColumn: ColumnDef<DataTableRow<PurchaseOrder>> = {
    id: ORDER_COLUMN_IDS.SHIP_TO_FLAGS,
    accessorFn: (row) => getShipToFlagCountryCode(row.shippingAddress) ?? "",
    header: OrderHeaderCell,
    cell: OrderShipToFlagsCell,
    enableSorting: false,
    meta: { exportHeader: t("views.orders.columns.shipToFlags") },
  };

  return [
    {
      accessorKey: "purchaseOrderId",
      id: ORDER_COLUMN_IDS.ORDER_ID,
      header: OrderHeaderCell,
      cell: (props) => <OrderIdCell {...props} onViewDetails={onOrderClick} />,
      meta: { exportHeader: t("views.orders.columns.order") },
    },
    // Hidden — readable date string for CSV export
    {
      id: ORDER_COLUMN_IDS.CREATED_AT_STRING,
      accessorFn: (row) => (row.createdAt ? t("dates.relative", { date: parseISO(row.createdAt) }) : ""),
      header: OrderHeaderCell,
      cell: OrderCreatedAtCell,
      enableSorting: false,
      meta: { exportHeader: t("views.orders.columns.created") },
    },
    // Visible — grouping column (grouped by date)
    {
      id: ORDER_COLUMN_IDS.CREATED_AT,
      accessorFn: (row) => (row.createdAt ? t("dates.short", { date: parseISO(row.createdAt) }) : ""),
      header: OrderHeaderCell,
      cell: OrderCreatedAtCell,
      sortingFn: sortByCreatedAt,
      getGroupingValue: (row) => {
        const d = new Date(row.createdAt);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      },
    },
    // Hidden — raw ISO createdAt for secondary sort tiebreaker
    {
      id: ORDER_COLUMN_IDS.CREATED_AT_WITH_TIME,
      accessorFn: (row) => row.createdAt,
      header: OrderHeaderCell,
      cell: OrderCreatedAtCell,
      sortingFn: sortByCreatedAt,
      enableGrouping: false,
    },
    // Hidden — expectedFulfillmentDate for primary sort
    {
      id: ORDER_COLUMN_IDS.EXPECTED_FULFILLMENT_DATE,
      accessorFn: (row) => {
        const date = row.expectedFulfillmentDate ?? row.createdAt;
        return date ? t("dates.short", { date: parseISO(date) }) : "";
      },
      header: OrderHeaderCell,
      cell: () => null,
      sortingFn: (rowA, rowB) => -compareByFulfillmentThenCreatedDesc(rowA.original, rowB.original),
      enableGrouping: false,
    },
    {
      id: ORDER_COLUMN_IDS.AGE,
      accessorFn: (row) => computeOrderAge(row)?.minutes ?? 0,
      header: OrderHeaderCell,
      cell: OrderAgeCell,
      meta: { exportHeader: t("views.orders.columns.age") },
    },
    {
      id: ORDER_COLUMN_IDS.STATUS,
      accessorFn: (row) => {
        if (!row.orderStatus) return "";
        const config = ORDER_STATUS_CONFIG[row.orderStatus];
        return config ? t(config.labelKey) : row.orderStatus;
      },
      header: OrderHeaderCell,
      cell: OrderStatusCell,
      meta: { exportHeader: t("views.orders.columns.status") },
    },
    {
      id: ORDER_COLUMN_IDS.BATCH,
      accessorFn: (row) => (row.batchId ? `BATCH-${readableBatchId(row.batchId)}` : ""),
      header: OrderHeaderCell,
      cell: (props) => <OrderBatchCell {...props} onBatchClick={onBatchClick} />,
      meta: { exportHeader: t("views.orders.columns.batch") },
    },
    {
      id: ORDER_COLUMN_IDS.MARKETPLACE,
      accessorFn: (row) => getMarketplaceDisplayName(row, carrierDetails),
      header: OrderHeaderCell,
      cell: (props) => <OrderMarketplaceCell {...props} carrierDetails={carrierDetails} />,
      meta: { exportHeader: t("views.orders.columns.marketplace") },
    },
    ...(showShipToCountry ? [shipToColumn] : []),
    ...(showShipToFlags ? [shipToFlagsColumn] : []),
    {
      id: ORDER_COLUMN_IDS.ITEMS,
      accessorFn: (row) =>
        row.lineItems
          ?.map((li) => {
            const title = li.productTitle ?? li.variantTitle ?? "";
            const quantity = li.quantity ? `${li.quantity}x ` : "";
            const grind = li.grindType ? ` - ${li.grindType}` : "";
            return `${quantity}${title}${grind}`.trim();
          })
          .join(", ") ?? "",
      header: OrderHeaderCell,
      cell: OrderItemsCell,
      meta: { exportHeader: t("views.orders.columns.items") },
    },
    {
      id: ORDER_COLUMN_IDS.WEIGHT,
      accessorFn: (row) => (row.orderWeightInGrams != null ? t("weight.weightWithSuffix", { grams: row.orderWeightInGrams }) : ""),
      header: OrderHeaderCell,
      cell: OrderWeightCell,
      meta: { exportHeader: t("views.orders.columns.weight") },
    },
    {
      accessorKey: "orderTotal",
      id: ORDER_COLUMN_IDS.ORDER_VALUE,
      header: OrderHeaderCell,
      cell: OrderValueCell,
      sortingFn: (rowA, rowB) => sortByNumber<PurchaseOrder>(rowA, rowB, "orderTotal"),
      meta: { exportHeader: t("views.orders.columns.orderTotal") },
    },
    {
      id: ORDER_COLUMN_IDS.TRACKING,
      accessorFn: (row) => {
        const unique = new Set((row.lineItems ?? []).map((li) => li.trackingNumber).filter((n): n is string => Boolean(n)));
        return Array.from(unique).join(", ");
      },
      header: OrderHeaderCell,
      cell: OrderTrackingCell,
      meta: { exportHeader: t("views.orders.columns.tracking") },
    },
    // Hidden — flattened shipping address for global search (street, city, zip, etc.)
    {
      id: ORDER_COLUMN_IDS.SHIPPING_ADDRESS,
      accessorFn: (row) => {
        const a = row.shippingAddress;
        if (!a) return "";
        return [
          a.name,
          a.firstName,
          a.lastName,
          a.company,
          a.address1,
          a.address2,
          a.city,
          a.province,
          a.provinceCode,
          a.country,
          a.countryCode,
          a.zip,
        ]
          .filter((v): v is string => Boolean(v))
          .join(" ");
      },
      header: OrderHeaderCell,
      cell: () => null,
      enableGrouping: false,
      enableSorting: false,
    },
  ];
}
